
import { CheckCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { Stream } from "@oculai/planner";
import MasonryWall from "@yeger/vue-masonry-wall";
import { parseISO, format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { Padding } from "photoswipe";
import "photoswipe/dist/photoswipe.css";
import { PhotoSwipe } from "photoswipe/dist/types/ui/ui-element";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import { defineComponent, PropType } from "vue";
import { segmentAnalytics } from "@/assets/scripts";
import LoadingSpinner from "@/components/loading_state/LoadingSpinner.vue";
import BasicModal from "@/components/modals/BasicModal.vue";
import { useStreams } from "@/composables/stream";
import CameraRepository from "@/repositories/CameraRepository";
import logger from "@/services/logger";
import { CameraImageItem } from "@/types/Camera";
import { ReportImageEntry, ReportImageEntrySelected } from "@/types/DailyReport";
import GalleryImageUpload from "@/views/daily_report/components/GalleryImageUpload.vue";

export default defineComponent({
  name: "ReportGallery",
  props: {
    images: {
      type: Object as PropType<ReportImageEntry[]>,
      required: true,
    },
    editMode: {
      type: Boolean,
      required: true,
    },
    highlightSection: {
      type: String,
      required: true,
    },
    approved: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
  },
  components: {
    PencilIcon,
    PlusIcon,
    CheckCircleIcon,
    BasicModal,
    MasonryWall,
    TrashIcon,
    GalleryImageUpload,
    LoadingSpinner,
  },
  emits: ["activeEditMode", "addHighlight", "updateGalleryImages", "removeGalleryImage"],

  data() {
    return {
      open: false as boolean,
      galleryMap: {} as Record<string, ReportImageEntrySelected[]>,
      date: this.$route.params.date,
      clickedImg: "",
      customImages: [] as ReportImageEntry[],
      lightbox: null as PhotoSwipeLightbox | null,
      error: false as boolean,
      noStreamData: false as boolean,
      loadingImages: true as boolean,
    };
  },
  mounted() {
    if (!this.lightbox) {
      const lightbox = new PhotoSwipeLightbox({
        gallery: ".gallery-images",
        children: "a",
        pswpModule: () => import("photoswipe"),
        padding: { top: 10, bottom: 10 } as Padding,
      });
      lightbox.on("uiRegister", function () {
        lightbox.pswp?.ui?.registerElement({
          name: "download-button",
          order: 8,
          isButton: true,
          tagName: "a",

          // SVG with outline
          html: {
            isCustomSVG: true,
            inner:
              '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1-1.5-1.6ZM23 23H9v2h14" id="pswp__icn-download"/>',
            outlineID: "pswp__icn-download",
          },

          onInit: (el: HTMLElement, pswp: PhotoSwipe) => {
            el.setAttribute("download", "");
            el.setAttribute("target", "_blank");
            el.setAttribute("rel", "noopener");

            pswp.on("change", () => {
              el.setAttribute("href", pswp.currSlide?.data.src as string);
            });
          },
        });
      });
      lightbox.init();
      this.lightbox = lightbox;
      lightbox.init();
      this.lightbox = lightbox;
    }
    this.resizeImagesLightbox();
  },
  unmounted() {
    if (this.lightbox) {
      this.lightbox?.destroy();
      this.lightbox = null;
    }
  },
  setup() {
    const { streams, isLoading } = useStreams();
    return { streams, isLoading };
  },
  watch: {
    images() {
      this.resizeImagesLightbox();
    },
  },
  computed: {
    sortedStreams() {
      return this.streams.slice().sort((a, b) => a.order - b.order);
    },
  },
  methods: {
    resizeImagesLightbox() {
      this.images.forEach((img: ReportImageEntry) => {
        const adjustedImage = img as ReportImageEntry;
        const imgElement = new Image();
        imgElement.src = String(img.url);
        imgElement.addEventListener("load", () => {
          adjustedImage.width = imgElement.naturalWidth;
          adjustedImage.height = imgElement.naturalHeight;
        });
      });
    },
    closeModal() {
      this.open = false;
      this.customImages = [];
    },
    addCustom(value: ReportImageEntry[]) {
      segmentAnalytics("dcr-custom-images-upload");
      this.customImages = value;
    },
    activeEditMode() {
      this.$emit("activeEditMode");
      this.$emit("addHighlight");
    },
    getImages(stream: Stream) {
      return CameraRepository.getPaginatedGalleryImages(
        stream.customer_name,
        stream.site_id,
        stream.camera_id,
        this.date as string,
      )
        .then((response) => {
          if (response.items.length > 0) {
            const middleIndex = Math.floor(response.items.length / 2);
            const cameraImg: CameraImageItem[] =
              response.items.length < 4
                ? response.items
                : [
                    response.items[0],
                    response.items[middleIndex],
                    response.items[response.items.length - 1],
                  ];
            const imgs: ReportImageEntrySelected[] = cameraImg.map((img) => {
              return {
                url: img.url,
                timestamp: this.convertToISO(img.timestamp),
                camera_id: stream.camera_id,
                name: stream.name,
                selected: false,
              };
            });
            this.galleryMap[stream.camera_id] = imgs;
          }
        })
        .catch((e) => {
          if (e?.response?.status !== 404) {
            logger.error(e);
          }
        });
    },

    formatDate(value: string) {
      const parseUtcDate = utcToZonedTime(parseISO(value), "UTC");
      return format(parseUtcDate, "HH:mm");
    },

    convertToISO(timestamp: string) {
      const dateItem = timestamp !== "" ? new Date(timestamp) : "";
      return dateItem !== ""
        ? zonedTimeToUtc(dateItem, "UTC").toISOString().replace("Z", "+00:00")
        : "";
    },
    async openModal() {
      this.activeEditMode();
      this.loadingImages = true;
      this.open = true;
      this.galleryMap = {};
      await Promise.all(this.streams.map((stream) => this.getImages(stream)));
      for (const key in this.galleryMap) {
        this.galleryMap[key].forEach(
          (img) =>
            (img.selected = this.images.some((item: ReportImageEntry) => {
              return img.timestamp === item.timestamp && img.camera_id === item.camera_id;
            })),
        );
      }
      this.loadingImages = false;
    },

    updateGalleryImages() {
      this.$emit("updateGalleryImages", {
        galleryMap: this.galleryMap,
        customImages: this.customImages,
      });
      this.customImages = [];
      this.resizeImagesLightbox();
      this.$emit("activeEditMode");
      this.open = false;
    },
    toggleImageSelection(image: ReportImageEntrySelected) {
      image.selected = !image.selected;
    },
  },
});
